import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import { ToastContainer } from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';
import { adminAPI, hrAPI } from '../../api';
import HRStats from './components/HRStats';
import AttendanceTab from './components/AttendanceTab';
import SalaryTab from './components/SalaryTab';
import PayrollTab from './components/PayrollTab';

const HRManagementPage = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('attendance');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const { toasts, removeToast, showSuccess, showError } = useToast();

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await hrAPI.getStats();
      setStats(data);
    } catch (error) {
      showError(error.message || 'Unable to load HR stats');
    } finally {
      setStatsLoading(false);
    }
  }, [showError]);

  const fetchEmployees = useCallback(async () => {
    try {
      setEmployeesLoading(true);
      const response = await adminAPI.listUsers({ page: 1, limit: 100, status: 'active' });
      const payload = response?.data || {};
      const users = payload.users || payload.data?.users || [];
      const options = users.map((user) => ({
        value: user._id,
        label: user.name || user.displayName || user.email,
        role: user.role
      }));
      setEmployeeOptions(options);
    } catch (error) {
      showError(error.message || 'Unable to load employee directory');
    } finally {
      setEmployeesLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchStats();
    fetchEmployees();
  }, [fetchStats, fetchEmployees]);

  const tabConfig = useMemo(() => ([
    { id: 'attendance', label: 'Attendance', icon: 'CalendarCheck2', component: AttendanceTab },
    { id: 'salary', label: 'Salary', icon: 'BadgeDollarSign', component: SalaryTab },
    { id: 'payroll', label: 'Payroll', icon: 'Files', component: PayrollTab }
  ]), []);

  const ActiveComponent = tabConfig.find((tab) => tab.id === activeTab)?.component || AttendanceTab;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
      />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <main className={`transition-all duration-300 pt-16 ${isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-60'}`}>
        <div className="p-4 lg:p-6 space-y-6">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-blue-600 text-sm font-semibold">
                  <Icon name="Users" size={18} />
                  Admin Only
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mt-1">HR Management</h1>
                <p className="text-gray-600 mt-1">Attendance, salary, and payroll controls in one workspace</p>
              </div>
              <button
                onClick={() => {
                  fetchStats();
                  fetchEmployees();
                }}
                className="hidden md:inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <Icon name="RefreshCcw" size={16} className="mr-2" />
                Sync Data
              </button>
            </div>
          </div>

          <HRStats stats={stats} loading={statsLoading} onRefresh={fetchStats} />

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
            <div className="border-b border-gray-100 overflow-x-auto">
              <nav className="flex space-x-2 px-4" aria-label="HR Tabs">
                {tabConfig.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'border-blue-500 text-blue-600 bg-blue-50/50 rounded-t-lg' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
            <div className="p-6">
              <ActiveComponent
                employeeOptions={employeeOptions}
                employeesLoading={employeesLoading}
                showSuccess={showSuccess}
                showError={showError}
                onDataChanged={fetchStats}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HRManagementPage;
